/*
    Holdout Test Results (MPN-1688).

    Replaces a Google Sheet that Data Analytics maintained by exporting from the old platform.
    Three tabs: a portfolio Overview of pooled rollups, By Campaign at the grain the results
    are actually measured on -- one row per campaign and Target Action -- and the Use Case to
    Funnel Stage mapping that drives the rollups.

    INTERNAL ONLY, and the banner says so. This is every client's lift on one page; showing
    one school another school's results is the worst thing it could do, which is why the
    endpoint behind it is gated on PLATFORM_ADMIN rather than on anything a customer holds.

    No arithmetic here. Every figure arrives calculated so that the page, the CSV and the
    sheet this replaces cannot disagree about a number.

    The layout follows the approved prototype. Before that pass this module had no stylesheet
    at all: Bootstrap utilities inside an otherwise MUI shell, which is why it did not look
    like the rest of the platform. Styles now live in holdout.scss.
*/
import {
    AlertOctagon, BatteryMedium, Building2, Calendar, CheckCircle2, EyeOff, LayoutGrid,
    PieChart, Percent, TrendingUp, Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { PermissionLevels } from "@models/permission.model";
import {
    getHoldoutReport, type HoldoutReport, type PooledRow,
} from "@services/analytics/holdoutReportService";
import { CHANNEL_HOLDOUT, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import { appStore } from "@store/appStore";

import ByCampaignTable from "./ByCampaignTable";
import ChannelHoldoutResults from "./ChannelHoldoutResults";
import ColumnInfo from "./ColumnInfo";
import DateRangePicker from "./DateRangePicker";
import ExcludeRowModal from "./ExcludeRowModal";
import ExcludedRows from "./ExcludedRows";
import MethodologyPanel from "./MethodologyPanel";
import { SrmPill, TierPill } from "./StatusPill";
import UseCaseMappingTab from "./UseCaseMappingTab";
import {
    dataAsOf, EMPTY, filterRows, pp, pValue, rangeLabel, rate, relativeLift, stamp, toCsv,
    type HoldoutFilters, type HoldoutRow, type Lens, type PresetKey, type ReportScope,
} from "./holdoutReport";

import "./holdout.scss";

type TileIcon = typeof CheckCircle2;

const TILES: { key: string; label: string; suffix?: string; icon: TileIcon; alarm?: boolean }[] = [
    { key: "campaigns_reaching_significance", label: "Campaigns Reaching Significance", icon: CheckCircle2 },
    { key: "pct_campaigns_reaching_significance", label: "% Campaigns Reaching Significance", suffix: "%", icon: Percent },
    { key: "clients_reaching_significance", label: "Clients Reaching Significance", icon: Building2 },
    { key: "pct_clients_reaching_significance", label: "% Clients Reaching Significance", suffix: "%", icon: PieChart },
    { key: "avg_relative_lift_proven", label: "Avg. Relative Lift (Proven)", suffix: "%", icon: TrendingUp },
    { key: "campaigns_in_holdout_testing", label: "Campaigns in Holdout Testing", icon: Users },
    // The only tile that changes colour. A count of campaigns whose split is wrong is the one
    // figure here that should be noticed before it is read.
    { key: "campaigns_flagged_for_srm", label: "Campaigns Flagged for SRM", icon: AlertOctagon, alarm: true },
    { key: "campaigns_underpowered", label: "Campaigns Underpowered", icon: BatteryMedium },
];

const TABS: { key: TabKey; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "campaigns", label: "By Campaign" },
    { key: "mapping", label: "Use Case Mapping" },
];

type TabKey = "overview" | "campaigns" | "mapping";

export default function HoldoutReportPage() {
    const [report, setReport] = useState<HoldoutReport | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState<TabKey>("overview");
    const [filters, setFilters] = useState<HoldoutFilters>({});
    const [excluding, setExcluding] = useState<HoldoutRow | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [scope, setScope] = useState<ReportScope>({});
    const [preset, setPreset] = useState<PresetKey>("all");
    const [lens, setLens] = useState<Lens>("results");
    // Frozen on the first, unnarrowed load. Built from the rows it would collapse to the one
    // client already selected, and there would be no way back to the rest of the portfolio.
    const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
    const channelHoldoutOn = useFeatureFlag(CHANNEL_HOLDOUT);

    /*
        Who may change what, read once (MPN-2046).

        Opening the report and editing it are different acts. Excluding a row writes an audit
        record under somebody's name and moves every figure on the page; editing the Use Case
        mapping re-files a whole portfolio. Both were offered to anybody who could reach the
        page, and the only thing stopping a read-only viewer was the server refusing after the
        click -- which looks like a broken button rather than a boundary.

        TWO GATES, not one. The report has its own feature now: it is read by Data Analysts,
        and Platform Admin additionally carries role and feature mutation, tracer reads and
        system commands -- gating a report on it meant the Analytics role, which the banner
        names as the audience, could not open the page at all.

        Excluding a row takes "Holdout Report" at EDIT. Changing the Use Case mapping stays
        Platform Admin: repointing a use case re-files every campaign set to it, portfolio-wide,
        which is a different kind of decision from taking one row out of one report. The server
        enforces the same split; these only decide what is drawn.
    */
    const permissions = appStore.loggedInUser?.permissions;
    const isPlatformAdmin = permissions
        ?.hasSomeAccess?.("Platform Admin", PermissionLevels.UPDATE) ?? false;
    const canExclude = (permissions
        ?.hasSomeAccess?.("Holdout Report", PermissionLevels.UPDATE) ?? false) || isPlatformAdmin;

    useEffect(() => {
        getHoldoutReport(scope)
            .then((data) => {
                setReport(data);
                setClients((known) => (known.length ? known : clientsIn(data.rows)));
            })
            .catch((e) => setError(e instanceof Error ? e.message : "Could not load the report."));
    }, [reloadKey, scope]);

    const rows = report?.rows ?? [];
    const visible = useMemo(() => filterRows(rows, filters), [rows, filters]);

    const download = () => {
        // A blob rather than a server round trip: the acceptance criteria ask for the
        // currently filtered rows, and those only exist here.
        const blob = new Blob([toCsv(visible)], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `holdout-test-results-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (error) return <div className="alert alert-danger m-4" role="alert">{error}</div>;
    if (!report) return <div className="p-4 text-muted">Loading holdout results...</div>;

    const excluded = report.excluded ?? [];
    const clientName = clients.find((c) => c.id === scope.organizationId)?.name;

    return (
        <div className="holdout-report p-4">
            <div className="holdout-report__banner" role="alert">
                <span className="holdout-report__banner-mark" aria-hidden="true">!</span>
                <span className="holdout-report__banner-label">INTERNAL ONLY</span>
                <span>Data Analysts, Ops and leadership. Never share with or expose to clients.</span>
            </div>

            <div className="holdout-report__header">
                <div className="holdout-report__identity">
                    <div className="holdout-report__titleline">
                        {/* The chip is the only thing on the page that says whose results
                            these are. An icon in front of it makes it read as a scope rather
                            than as part of the title. */}
                        <span className="holdout-report__scope">
                            <LayoutGrid size={12} aria-hidden="true" />
                            {clientName ?? "All organizations"}
                        </span>
                        <span className="holdout-report__accent" aria-hidden="true" />
                        <h1 className="holdout-report__title">Holdout Test Results</h1>
                        <span className="holdout-report__badge">PORTFOLIO</span>
                    </div>
                    {/* Freshness in the header rather than buried: the figures move when
                        conversion files are processed, and "why does this disagree with what I
                        saw yesterday" is answered by the date rather than by anything below. */}
                    <p className="holdout-report__freshness">
                        Reach &middot; Portfolio &middot; {rangeLabel(scope, preset)} &middot;{" "}
                        Data as of {stamp(dataAsOf(rows))}
                        <ColumnInfo column="last_calculated" />
                    </p>
                </div>

                <ScopeControls scope={scope} setScope={setScope} clients={clients}
                    preset={preset} setPreset={setPreset} />
            </div>

            <div className="holdout-report__tabs" role="tablist">
                {TABS.map((entry) => (
                    <button key={entry.key} type="button" role="tab"
                        aria-selected={tab === entry.key}
                        className={`holdout-report__tab ${tab === entry.key ? "holdout-report__tab--on" : ""}`}
                        onClick={() => setTab(entry.key)}>
                        {entry.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && (
                <p className="holdout-report__lede">
                    Lift and statistical significance across all campaigns currently running a
                    randomized holdout test, rolled up by funnel stage and target action.
                </p>
            )}

            {/* Why the figures read as they do, on the tab where they are read. The excluded
                table itself lives under By Campaign, which is no help to somebody looking at a
                tile and wondering what is missing from it. */}
            {tab === "overview" && excluded.length > 0 && (
                <div className="holdout-report__excluded-note">
                    <EyeOff size={14} aria-hidden="true" />
                    <span>
                        <strong>
                            {excluded.length} {excluded.length === 1 ? "row" : "rows"} excluded
                            from this report
                        </strong>{" "}
                        by a Data Analyst. Every figure here is recalculated without them. The
                        campaign data is unchanged.
                    </span>
                    <a href="#holdout-excluded" onClick={() => setTab("campaigns")}>
                        Review or Restore &rarr;
                    </a>
                </div>
            )}

            {tab === "mapping" ? (
                <UseCaseMappingTab canEdit={isPlatformAdmin} />
            ) : tab === "overview" ? (
                <Overview report={report} showChannelHoldouts={channelHoldoutOn} />
            ) : (
                <>
                    <ByCampaignTable
                        rows={rows} visible={visible}
                        filters={filters} setFilters={setFilters}
                        lens={lens} setLens={setLens}
                        onExport={download} onExclude={setExcluding}
                        canExclude={canExclude}
                    />
                    {/* From the same response as the tiles. Two fetches could disagree if
                        somebody excluded a row between them, and the table would then be
                        explaining figures it no longer matches. */}
                    <ExcludedRows rows={excluded} canRestore={canExclude}
                        onRestored={() => setReloadKey((key) => key + 1)} />
                </>
            )}

            {excluding && (
                <ExcludeRowModal
                    row={excluding}
                    onClose={() => setExcluding(null)}
                    onDone={() => {
                        setExcluding(null);
                        // Refetched rather than spliced out locally: excluding a row also
                        // changes every rollup and tile computed from it, and recomputing
                        // those here would be a second implementation of the pooling.
                        setReloadKey((key) => key + 1);
                    }}
                />
            )}
        </div>
    );
}

/**
 * Date range and client, which narrow the whole report rather than one table.
 *
 * Separate from the By Campaign column filters on purpose. Those hide rows on screen; these
 * change what the server pooled, so the Overview tiles and rollups move with them. Filtering
 * the table alone would leave the tiles describing a portfolio the table no longer shows.
 */
function ScopeControls({ scope, setScope, clients, preset, setPreset }: {
    scope: ReportScope; setScope: (s: ReportScope) => void;
    clients: { id: number; name: string }[];
    preset: PresetKey; setPreset: (p: PresetKey) => void;
}) {
    const isDefault = preset === "all" && !scope.organizationId;
    return (
        <div className="holdout-report__controls">
            <span className="holdout-report__control-label">
                <Calendar size={13} className="me-1" aria-hidden="true" />
                Date Range
                {/* Narrowing the range changes what the rates mean, not just which rows are
                    counted. That is the kind of thing this report explains rather than leaves
                    somebody to infer from figures that moved. */}
                <ColumnInfo column="date_range" />
            </span>
            <DateRangePicker scope={scope} preset={preset}
                onChange={(next, nextPreset) => { setScope(next); setPreset(nextPreset); }} />
            <select aria-label="Client" className="form-select form-select-sm w-auto"
                value={scope.organizationId ?? ""}
                onChange={(e) => setScope({
                    ...scope, organizationId: Number(e.target.value) || undefined,
                })}>
                <option value="">All clients</option>
                {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                ))}
            </select>
            {!isDefault && (
                <button type="button" className="btn btn-sm btn-link px-0"
                    onClick={() => { setScope({}); setPreset("all"); }}>Clear</button>
            )}
        </div>
    );
}

/** The clients present, for the picker. Sorted, so the list does not reorder between loads. */
function clientsIn(rows: HoldoutRow[]): { id: number; name: string }[] {
    const byId = new Map<number, string>();
    rows.forEach((row) => { if (row.client_id) byId.set(row.client_id, row.client); });
    return [...byId].map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The columns a pooled table can show, and which of them each table shows.
 *
 * Per table rather than one shared set, because the four tables answer different questions.
 * Target Action is read across a lot of rows and drops the interval bounds to stay legible;
 * Overall has no label column because there is only ever one row; Use Case carries both its
 * own name and the lifecycle category it maps to.
 */
export type ColumnKey =
    | "ownStage" | "stage" | "useCase" | "action"
    | "treatment" | "control" | "abs" | "absLo" | "absHi"
    | "rel" | "relLo" | "relHi" | "tier" | "p" | "dateReached" | "srm";

const STATS: ColumnKey[] = [
    "treatment", "control", "abs", "absLo", "absHi", "rel", "relLo", "relHi", "tier", "p",
];

const COLUMNS: Record<ColumnKey, { label: string; info?: string; numeric?: boolean; bound?: boolean }> = {
    // Two ways a row can be in a funnel stage, and they read from different fields.
    // `ownStage` is for the rollup that groups BY stage, where the stage is the row's own
    // identity and arrives as `label`. `stage` is for the rollups grouped by something else,
    // where the server sends the stage alongside as `funnel_stage_label`. Using one for the
    // other renders every row as "Uncategorized", because the field it wants is simply absent.
    ownStage: { label: "Funnel stage", info: "funnel_stage" },
    stage: { label: "Funnel stage", info: "funnel_stage" },
    useCase: { label: "Use case", info: "use_case" },
    action: { label: "Target action", info: "rollup" },
    treatment: { label: "Treatment conv.", info: "treatment_rate", numeric: true },
    control: { label: "Control conv.", info: "control_rate", numeric: true },
    abs: { label: "Abs. lift", info: "absolute_lift", numeric: true },
    absLo: { label: "Abs. lift (lower)", info: "absolute_ci", numeric: true, bound: true },
    // The upper bound shares its definition with the lower one. It carries the icon anyway:
    // "every column header has an info icon" is the rule, and a reader scanning one column
    // should not have to know the pair is defined elsewhere (Holly, QA).
    absHi: { label: "Abs. lift (upper)", info: "absolute_ci", numeric: true, bound: true },
    rel: { label: "Relative lift", info: "relative_lift", numeric: true },
    relLo: { label: "Rel. lift (lower)", info: "relative_ci", numeric: true, bound: true },
    relHi: { label: "Rel. lift (upper)", info: "relative_ci", numeric: true, bound: true },
    tier: { label: "Confidence tier", info: "tier" },
    p: { label: "P-value", info: "p_value", numeric: true },
    dateReached: { label: "Date reached", info: "date_reached", numeric: true },
    srm: { label: "SRM check", info: "srm_check" },
};

function Overview({ report, showChannelHoldouts }: {
    report: HoldoutReport; showChannelHoldouts: boolean;
}) {
    return (
        <>
            <div className="holdout-report__tiles">
                {TILES.map((tile) => {
                    const value = report.tiles[tile.key];
                    const Icon = tile.icon;
                    const alarm = tile.alarm && typeof value === "number" && value > 0;
                    return (
                        <div className="holdout-report__tile" key={tile.key}>
                            <span className="holdout-report__tile-icon" aria-hidden="true">
                                <Icon size={17} />
                            </span>
                            <span className="holdout-report__tile-text">
                                <span className="holdout-report__tile-label">{tile.label}</span>
                                <span className={`holdout-report__tile-value${alarm ? " holdout-report__tile-value--alarm" : ""}`}>
                                    {formatTile(value, tile.suffix)}
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className="holdout-report__sections">
                <PooledTable
                    title="Overall"
                    caption="A single pooled row for every campaign in holdout testing."
                    rows={[report.overall]}
                    columns={[...STATS, "dateReached", "srm"]}
                />
                <PooledTable
                    title="Rollup by Funnel Stage"
                    caption={
                        <>
                            Funnel stage here is the <strong>lifecycle category</strong> (Lead
                            &rarr; Purchase, Retention, Churn) derived from Use Case, not the
                            student-journey stages on client reporting.
                        </>
                    }
                    rows={report.by_funnel_stage}
                    columns={["ownStage", ...STATS, "srm"]}
                />
                <PooledTable
                    title="Rollup by Use Case"
                    caption="Pooled by the campaign-level Use Case field. Funnel stage is the lifecycle category each Use Case maps to."
                    rows={report.by_use_case}
                    columns={["useCase", "stage", ...STATS, "srm"]}
                />
                <PooledTable
                    title="Rollup by Target Action"
                    caption="Pooled per Journey Step within each lifecycle category. Dimensions on a Journey Step aggregate into it."
                    rows={report.by_target_action}
                    columns={["stage", "action", "treatment", "control", "abs", "rel", "tier", "p", "srm"]}
                />
                {showChannelHoldouts && (
                    <ChannelHoldoutResults rows={report.channel_holdouts ?? []} />
                )}
                <MethodologyPanel />
            </div>
        </>
    );
}

function PooledTable({ title, caption, rows, columns }: {
    title: string; caption?: React.ReactNode; rows: PooledRow[]; columns: ColumnKey[];
}) {
    return (
        <section>
            <div className="holdout-report__section-head">
                {/* No row-count badge. On the Overview the counts are the subject of the
                    tables themselves -- "Rollup by Funnel Stage 3" beside three visible rows
                    is a label counting what is already in front of you (Holly, QA,
                    7 Oct 2026). By Campaign keeps its count, where the table is filtered and
                    the number says what is hidden. */}
                <div className="holdout-report__section-title">
                    <h2>{title}</h2>
                </div>
                {caption && <p className="holdout-report__caption">{caption}</p>}
            </div>
            <div className="holdout-report__table-wrap">
                <table className="holdout-report__table">
                    <thead>
                        <tr>
                            {columns.map((key) => {
                                const column = COLUMNS[key];
                                return (
                                    <th key={key} className={column.numeric ? "is-num" : undefined}>
                                        {column.label}
                                        {column.info && <ColumnInfo column={column.info} />}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={row.key ?? index}>
                                {columns.map((key) => (
                                    <Cell key={key} column={key} row={row} />
                                ))}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className="is-empty">
                                    No campaigns in holdout testing.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

const NAME_COLUMNS = new Set<ColumnKey>(["ownStage", "stage", "useCase", "action"]);

function Cell({ column, row }: { column: ColumnKey; row: PooledRow }) {
    const meta = COLUMNS[column];
    const className = [
        meta.numeric ? "is-num" : "",
        meta.bound ? "is-bound" : "",
        NAME_COLUMNS.has(column) ? "is-name" : "",
    ].filter(Boolean).join(" ") || undefined;

    return <td className={className}>{cellValue(column, row)}</td>;
}

export function cellValue(column: ColumnKey, row: PooledRow): React.ReactNode {
    switch (column) {
        case "ownStage":
        case "useCase":
        case "action": return row.label ?? row.key ?? "Uncategorized";
        case "stage": return row.funnel_stage_label ?? "Uncategorized";
        case "treatment": return rate(row.treatment_rate);
        case "control": return rate(row.control_rate);
        case "abs": return pp(row.absolute_lift);
        case "absLo": return pp(row.absolute_lift_lower);
        case "absHi": return pp(row.absolute_lift_upper);
        // Positive lift is the thing the page exists to find, so it is the one figure that
        // earns colour. Green and semibold on a gain; everything else stays neutral rather
        // than painting losses red, which would read as a fault rather than a result.
        case "rel": return (
            <span className={liftClass(row.absolute_lift)}>
                {relativeLift(row.relative_lift, row.control_rate)}
            </span>
        );
        case "relLo": return relativeLift(row.relative_lift_lower, row.control_rate);
        case "relHi": return relativeLift(row.relative_lift_upper, row.control_rate);
        case "tier": return <TierPill tier={row.tier} />;
        case "p": return pValue(row.p_value);
        // Always blank, and the column exists anyway. A pooled row has no single date it
        // became significant -- the campaigns under it reached significance on different days,
        // or never -- and the column keeps this table's shape aligned with By Campaign.
        case "dateReached": return <span className="is-empty">{EMPTY}</span>;
        case "srm": return <SrmPill check={row.srm_check} />;
        default: return null;
    }
}

/**
 * A tile value.
 *
 * The server rounds the average relative lift to two decimals and the percentage tiles to
 * one, so the row of tiles mixed precisions -- "184.27%" beside "5.9%". Rounded here to a
 * whole percent: these are portfolio headlines, and a hundredth of a percent on an average
 * of averages is noise dressed as precision (Holly, QA, 6 Oct 2026).
 */
function formatTile(value: number | null | undefined, suffix?: string): string {
    if (value === null || value === undefined) return EMPTY;
    const shown = suffix === "%" ? Math.round(value) : value;
    return `${shown}${suffix ?? ""}`;
}

/** Green and semibold on a gain. Driven by the absolute difference, which is the figure that
    is defined even when the relative one is not. */
function liftClass(absoluteLift: number | null | undefined): string {
    return absoluteLift !== null && absoluteLift !== undefined && absoluteLift > 0
        ? "holdout-report__lift--up"
        : "";
}
